import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Button } from '@/components/ui/button';
import { useUploadAttachment, validateAttachmentFile } from '@/hooks/use-attachments';
import { toast } from '@/hooks/use-toast';
import { ATTACHMENT_MIME_TYPES } from '@/lib/api/attachments';
import { Loader2, Upload } from 'lucide-react';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
export function AttachmentUploadButton({ studyId, disabled }) {
    const { t } = useTranslation();
    const inputRef = useRef(null);
    const uploadMutation = useUploadAttachment();
    const accept = Object.keys(ATTACHMENT_MIME_TYPES).join(',');
    async function handleFiles(files) {
        if (!files || files.length === 0)
            return;
        // Files upload sequentially: each gets its own signed URL + finalize, and
        // we want the per-file error surface to be clear if one fails.
        for (const file of Array.from(files)) {
            const validationError = validateAttachmentFile(file);
            if (validationError) {
                toast({ title: 'Upload failed', description: validationError, variant: 'destructive' });
                continue;
            }
            try {
                // react-doctor-disable-next-line react-doctor/async-await-in-loop -- one upload at a time, so each file gets its own signed URL, finalize and error toast
                await uploadMutation.mutateAsync({ studyId, file });
                toast({ title: 'Attachment uploaded', description: file.name });
            }
            catch (err) {
                toast({
                    title: 'Upload failed',
                    description: err instanceof Error ? err.message : 'Unknown error',
                    variant: 'destructive',
                });
            }
        }
        if (inputRef.current)
            inputRef.current.value = '';
    }
    return (_jsxs(_Fragment, { children: [_jsx("input", { ref: inputRef, type: "file", multiple: true, className: "hidden", accept: accept, onChange: (e) => handleFiles(e.target.files) }), _jsxs(Button, { type: "button", size: "sm", variant: "outline", disabled: disabled || uploadMutation.isPending, onClick: () => inputRef.current?.click(), children: [uploadMutation.isPending ? (_jsx(Loader2, { className: "mr-2 h-4 w-4 animate-spin" })) : (_jsx(Upload, { className: "mr-2 h-4 w-4" })), t('attachments.upload')] })] }));
}
